<template>
    <div>
        <pc-label
            v-if="label"
            v-bind="labelBinder"
            v-on:help-clicked="$emit('help-clicked', $event)" />
        <template v-if="noAddOns">
            <input
                v-bind="binder"
                ref="input"
                v-model="displayValue"
                v-bind:maxlength="maxlength"
                v-bind:inputmode="inputmode"
                v-bind:required="required"
                v-bind:aria-invalid="hasValidationError"
                v-bind:aria-describedby="validationErrorId"
                class="form-control d-block d-print-none"
                no-interact
                v-on="listeners" />
            <div class="d-none d-print-block form-control height-corrections">
                {{ displayValue }}
            </div>
            <div
                v-if="showRemainingCharacters && !inPrintStyle"
                class="d-flex align-items-center justify-content-end mb-3">
                <div class="text-body-secondary fst-italic small">
                    {{ remainingCharacters }}
                </div>
            </div>
            <div
                v-if="showValidationErrors"
                v-bind:id="validationErrorId"
                v-bind:class="{ 'd-block': hasValidationError }"
                class="invalid-feedback fw-bold">
                {{ validationError }}
            </div>
        </template>
        <template v-else>
            <div class="input-group">
                <template v-if="hasPrepend">
                    <span
                        v-for="(item, index) in prependItems"
                        v-bind:key="index"
                        v-bind:class="{ 'invalid-add-on': hasValidationError }"
                        class="input-group-text"
                        >{{ item }}</span
                    >
                </template>
                <input
                    v-bind="binder"
                    ref="input"
                    v-model="displayValue"
                    v-bind:maxlength="maxlength"
                    v-bind:inputmode="inputmode"
                    v-bind:required="required"
                    v-bind:aria-describedby="validationErrorId"
                    class="form-control d-block d-print-none"
                    v-on="listeners" />
                <div
                    v-if="inPrintStyle"
                    class="d-none d-print-block form-control height-corrections">
                    {{ displayValue }}
                </div>
                <template v-if="hasAppend">
                    <span
                        v-for="(item, index) in appendItems"
                        v-bind:key="index"
                        v-bind:class="{ 'invalid-add-on': hasValidationError }"
                        class="input-group-text"
                        >{{ item }}</span
                    >
                </template>
                <template v-if="hasManualAppend">
                    <slot name="manualAppend" />
                </template>
            </div>
            <div
                v-if="showRemainingCharacters && !inPrintStyle"
                class="d-flex align-items-center justify-content-end mb-3">
                <div class="text-body-secondary fst-italic small">
                    {{ remainingCharacters }}
                </div>
            </div>
            <div
                v-if="showValidationErrors"
                v-bind:id="validationErrorId"
                v-bind:class="{ 'd-block': hasValidationError }"
                class="invalid-feedback fw-bold">
                {{ validationError }}
            </div>
        </template>
    </div>
</template>

<script>
    import base from "./_inputBase.vue";
    import { KEYS, NAMESPACE } from "./i18n/pcInput.i18n";
    import { mapI18n } from "Util/i18n";

    export default {
        extends: base,
        props: {
            type: { type: String, required: false, default: null },
            prependItems: { type: Array, required: false, default: () => [] },
            appendItems: { type: Array, required: false, default: () => [] },
            /**
             * A data formatter can include a function to format the stored value for display
             * and/or to unformat a value for storage.
             */
            dataFormatter: { type: Object, required: false, default: null },
            inputContainerClass: { type: String, required: false, default: null },
            maxlength: { type: Number, required: false, default: null },
            inputmode: { type: String, required: false, default: null },
            showRemainingCharacters: { type: Boolean, required: false, default: false },
            alwaysUpdateDisplayValue: { type: Boolean, required: false, default: false }
        },
        emits: ["help-clicked"],
        data: function () {
            if (this.type === "checkbox" || this.type === "radio" || this.type === "date" || this.type === "range") {
                console.warn("unsupported type");
            }
            return {
                internalType: this.type || "text",
                displayValue: this.modelValue,
                hasManualAppend: false
            };
        },
        computed: {
            inPrintStyle() {
                return this.$app.PrintStyling;
            },
            noAddOns() {
                return !this.hasPrepend && !this.hasAppend && !this.hasManualAppend;
            },
            hasPrepend() {
                return this.prependItems && this.prependItems.length > 0;
            },
            hasAppend() {
                return this.appendItems && this.appendItems.length > 0;
            },
            hasDataFormatter() {
                return this.dataFormatter !== null && this.dataFormatter !== undefined;
            },
            remainingCharacterCount() {
                if (this.showRemainingCharacters && !this.maxlength) {
                    throw "maxlength must be set to show remaining characters";
                }
                return this.maxlength - (this.displayValue?.length ?? 0);
            },
            ...mapI18n(
                {
                    ...KEYS,
                    remainingCharacters() {
                        return {
                            key: KEYS.remainingCharsLabel,
                            values: {
                                remainingChars: this.remainingCharacterCount
                            }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            displayValue(value) {
                /**
                 * pc-inputs decouple the internal value used by inputBase from
                 * a displayValue to allow displaying some type of formatted value
                 * without storing it. When the user updates the value of the input,
                 * we want to update the internalValue in real time (after unformatting),
                 * but we don't want the displayValue to be immediately updated when the
                 * internalValue changes
                 *
                 * Don't update the internalValue if it equals the unformatted value because
                 * this will result in an unnecessary "input" event on creation for pc-inputs
                 * with a data formatter, since they have to set the displayValue on creation
                 * by formatting the internalValue, but nothing has actually been changed.
                 */
                const unformatted = this.unformatValue(value);
                if (unformatted !== this.internalValue) {
                    this.internalValue = this.unformatValue(value);
                }
            },
            modelValue(value) {
                /**
                 * Because the displayValue is decoupled from the internalValue,
                 * if something externally modifies modelValue, we need to manually
                 * update displayValue. Don't update the display value if value
                 * changes as a result of the user typing in the input (ie. the
                 * input is still in focus)
                 */
                if (document.activeElement !== this.$refs.input || this.alwaysUpdateDisplayValue) {
                    this.displayValue = this.formatValue(value);
                }
            },
            dataFormatter(newValue, oldValue) {
                /**
                 * If the data formatter changes, we want the display value to be updated
                 * to reflect the new formatter.
                 */
                if (this.hasDataFormatter && this.dataFormatter.format) {
                    this.displayValue = this.dataFormatter.format(this.modelValue);
                } else if (newValue === null && oldValue !== null) {
                    this.displayValue = this.modelValue;
                }
            }
        },
        created() {
            this.displayValue = this.formatValue(this.modelValue);
        },
        mounted() {
            this.hasManualAppend = Boolean(this.$slots.manualAppend);
        },
        methods: {
            afterOnChange() {
                /**
                 * Only update the display value after the change event is fired.
                 * This allows the internalValue to stay updated in real time, but the
                 * displayValue only gets synced with it after the change event to
                 * avoid messiness with the cursor getting moved while typing.
                 */
                this.displayValue = this.formatValue(this.modelValue);
            },
            formatValue(value) {
                if (this.hasDataFormatter && this.dataFormatter.format) {
                    return this.dataFormatter.format(value);
                }
                return value;
            },
            unformatValue(value) {
                if (this.hasDataFormatter && this.dataFormatter.unformat) {
                    return this.dataFormatter.unformat(value);
                }
                return value;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .height-corrections {
        //Overrides styling heights for pc-inputs on printing so that they will at minimum
        //Be the height of the standard form-control input, but will expand to show all of the input as necessary.
        height: auto !important;
        min-height: var(--pc-input-height) !important;
    }

    .invalid-add-on {
        border-color: var(--bs-form-invalid-border-color);
    }
</style>
